import React from 'react';
import { Play, Copy, Edit, Send, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VoiceLayout } from './VoiceLayout';
import { VOICE_PROFILES_LIB, SCRIPT_TEMPLATES } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

export default function VoicesLibrary() {
  usePageTitle('Voice & Script Library');
  return (
    <VoiceLayout
      title="Voice & script library"
      subtitle="Approved voice profiles and reusable script templates governed by the compliance team."
    >
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Voice profiles</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {VOICE_PROFILES_LIB.map((v) => (
            <article key={v.id} className="surface-card p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold">{v.name}</p>
                  <p className="text-[11px] text-muted-foreground">Sample: {v.sample} · Tone: {v.tone}</p>
                </div>
                <Badge variant="outline" className="text-[10px] border-success/40 bg-success/10 text-success">v{v.version.replace('v','')}</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-md bg-muted/40 p-2"><p className="text-[9px] uppercase tracking-widest text-muted-foreground">Approved by</p><p>{v.approvedBy}</p></div>
                <div className="rounded-md bg-muted/40 p-2"><p className="text-[9px] uppercase tracking-widest text-muted-foreground">Usage</p><p className="font-mono-num">{formatNumber(v.usage)}</p></div>
                <div className="rounded-md bg-muted/40 p-2"><p className="text-[9px] uppercase tracking-widest text-muted-foreground">Score</p><p className="font-mono-num">{v.score}</p></div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"><Play className="h-3 w-3 mr-1" aria-hidden="true" />Preview</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"><Copy className="h-3 w-3 mr-1" aria-hidden="true" />Duplicate</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"><Edit className="h-3 w-3 mr-1" aria-hidden="true" />Edit</Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Script templates</h2>
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-widest">
              <tr>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Template</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Lang</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Tone</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Approved by</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Version</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Updated</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Status</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Usage</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Score</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {SCRIPT_TEMPLATES.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/40">
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-[12px]">{s.language}</td>
                  <td className="px-3 py-2 text-[12px]">{s.tone}</td>
                  <td className="px-3 py-2 text-[12px] text-muted-foreground">{s.approvedBy}</td>
                  <td className="px-3 py-2 text-[12px] font-mono-num">{s.version}</td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">{s.lastUpdated}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={cn('text-[10px]',
                      s.compliance === 'Passed' && 'border-success/40 bg-success/10 text-success',
                      s.compliance === 'Review' && 'border-warning/40 bg-warning/10 text-warning')}>
                      {s.compliance}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{formatNumber(s.usage)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{s.score || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" className="h-7 text-[11px]"><Play className="h-3 w-3 mr-1" aria-hidden="true" />Preview</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </VoiceLayout>
  );
}
